import { useEffect, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { IconMapPinOff } from "@tabler/icons-react";
import { useAuth } from "./lib/auth";
import { usePwa } from "./lib/pwa";
import { useI18n } from "./i18n";
import { ApplicantLayout, AppLayout, AuthLayout } from "./components/Layout";
import { ButtonLink, EmptyState } from "./components/ui";
import { ApplyPage, ForgotPasswordPage, InvitePage, LoginPage, ResetPasswordPage, VerifyEmailPage } from "./pages/AuthPages";
import { ApplicationPage } from "./pages/ApplicationPage";
import { CodePage, HomePage, ReferralDetailPage, ReferralsPage } from "./pages/ReferralPages";
import { WalletPage } from "./pages/WalletPage";
import { HelpPage, MorePage, NotificationsPage, ProfilePage } from "./pages/AccountPages";
import { TermsPage } from "./pages/TermsPage";

/** Brand splash while the session is checked. */
function Splash() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-canvas" role="status" aria-label="Loading">
      <img src="/icon-192.png" alt="" className="h-20 w-20 animate-pulse rounded-3xl shadow-raised" />
      <span className="h-1 w-24 overflow-hidden rounded-full bg-sunken">
        <span className="block h-full w-1/3 -translate-x-full animate-shimmer rounded-full bg-primary" />
      </span>
    </div>
  );
}

function NotFound() {
  const { t } = useI18n();
  const { signedIn, isAgent } = useAuth();
  return (
    <div className="py-10">
      <EmptyState
        icon={IconMapPinOff}
        title={t("notFoundTitle")}
        body={t("notFoundBody")}
        action={<ButtonLink to={!signedIn ? "/login" : isAgent ? "/" : "/application"}>{t("goHome")}</ButtonLink>}
      />
    </div>
  );
}

function GuestOnly({ children }: { children: ReactNode }) {
  const { signedIn, isAgent } = useAuth();
  return signedIn ? <Navigate to={isAgent ? "/" : "/application"} replace /> : <>{children}</>;
}

/** New page, top of the page (the browser restores scroll only on back/forward). */
function ScrollToTop() {
  const { pathname } = useLocation();
  // Block body on purpose: Chrome's window.scrollTo returns a Promise, and a
  // returned value becomes React's effect cleanup ("destroy is not a function").
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  const { loading, signedIn, isAgent, me } = useAuth();
  const { refreshPush } = usePwa();

  // Push state belongs to the signed-in session.
  useEffect(() => {
    if (signedIn) void refreshPush();
  }, [signedIn, me?.id, refreshPush]);

  if (loading) return <Splash />;

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public: sign-in and links from emails/SMS */}
        <Route path="/login" element={<GuestOnly><LoginPage /></GuestOnly>} />
        <Route path="/apply" element={<GuestOnly><ApplyPage /></GuestOnly>} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/invite" element={<InvitePage />} />

        {isAgent ? (
          <Route element={<AppLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/code" element={<CodePage />} />
            <Route path="/referrals" element={<ReferralsPage />} />
            <Route path="/referrals/:id" element={<ReferralDetailPage />} />
            <Route path="/wallet" element={<WalletPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/more" element={<MorePage />} />
            <Route path="/application" element={<Navigate to="/" replace />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        ) : signedIn ? (
          <Route element={<ApplicantLayout />}>
            <Route path="/application" element={<ApplicationPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/" element={<Navigate to="/application" replace />} />
            <Route path="*" element={<Navigate to="/application" replace />} />
          </Route>
        ) : (
          <>
            <Route path="/help" element={<AuthLayout wide><HelpPage /></AuthLayout>} />
            <Route path="/terms" element={<AuthLayout wide><TermsPage /></AuthLayout>} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        )}
      </Routes>
    </>
  );
}
